import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Tag, Share2, ArrowRight } from 'lucide-react';
import { SiWhatsapp } from 'react-icons/si';
import { BLOG_POSTS, getCategoryColor, formatDate, ROUTE_PATHS } from '@/lib/index';
import { springPresets } from '@/lib/motion';

// Simple markdown-like renderer for the post content
function MarkdownContent({ content }: { content: string }) {
  const lines = content.trim().split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (!line) {
      i++;
      continue;
    }

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="text-2xl font-bold mt-10 mb-4 text-foreground">
          {line.slice(3)}
        </h2>
      );
    } else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={i} className="text-xl font-bold mt-8 mb-3 text-foreground">
          {line.slice(4)}
        </h3>
      );
    } else if (line.startsWith('#### ')) {
      elements.push(
        <h4 key={i} className="text-lg font-semibold mt-6 mb-2 text-foreground">
          {line.slice(5)}
        </h4>
      );
    } else if (line.startsWith('```')) {
      // Code block
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <pre key={i} className="bg-muted border border-border rounded-xl p-4 overflow-x-auto my-4 text-sm font-mono text-muted-foreground">
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
    } else if (line.startsWith('- ')) {
      // Bullet list
      const items: string[] = [line.slice(2)];
      while (i + 1 < lines.length && lines[i + 1].trim().startsWith('- ')) {
        i++;
        items.push(lines[i].trim().slice(2));
      }
      elements.push(
        <ul key={i} className="list-none space-y-2 my-4">
          {items.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2 text-muted-foreground text-sm">
              <span className="text-primary mt-0.5">▸</span>
              <span dangerouslySetInnerHTML={{ __html: renderInline(item) }} />
            </li>
          ))}
        </ul>
      );
    } else if (/^\d+\./.test(line)) {
      // Numbered list
      const items: string[] = [line.replace(/^\d+\.\s*/, '')];
      while (i + 1 < lines.length && /^\d+\./.test(lines[i + 1].trim())) {
        i++;
        items.push(lines[i].trim().replace(/^\d+\.\s*/, ''));
      }
      elements.push(
        <ol key={i} className="list-none space-y-2 my-4 counter-reset-none">
          {items.map((item, idx) => (
            <li key={idx} className="flex items-start gap-3 text-muted-foreground text-sm">
              <span className="text-primary font-mono font-bold mt-0.5 w-5 flex-shrink-0">{idx + 1}.</span>
              <span dangerouslySetInnerHTML={{ __html: renderInline(item) }} />
            </li>
          ))}
        </ol>
      );
    } else if (line.startsWith('| ')) {
      // Table
      const tableLines: string[] = [line];
      while (i + 1 < lines.length && lines[i + 1].trim().startsWith('|')) {
        i++;
        tableLines.push(lines[i].trim());
      }
      const rows = tableLines.filter((l) => !l.includes('---'));
      const headers = rows[0].split('|').filter(Boolean).map((h) => h.trim());
      const dataRows = rows.slice(1).map((r) => r.split('|').filter(Boolean).map((c) => c.trim()));
      elements.push(
        <div key={i} className="overflow-x-auto my-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border">
                {headers.map((h, idx) => (
                  <th key={idx} className="text-left py-2 px-3 font-semibold text-foreground text-xs uppercase tracking-wide">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, ridx) => (
                <tr key={ridx} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                  {row.map((cell, cidx) => (
                    <td key={cidx} className="py-2 px-3 text-muted-foreground">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else {
      // Paragraph
      elements.push(
        <p key={i} className="text-muted-foreground leading-relaxed my-4 text-sm md:text-base" dangerouslySetInnerHTML={{ __html: renderInline(line) }} />
      );
    }

    i++;
  }

  return <div className="prose-custom">{elements}</div>;
}

function renderInline(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code class="font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded text-xs">$1</code>');
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  if (!post) return <Navigate to={ROUTE_PATHS.BLOG} replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== slug && p.category === post.category).slice(0, 2);

  return (
    <div className="min-h-screen bg-background">
      {/* Back */}
      <div className="pt-24 pb-4 border-b border-border">
        <div className="container mx-auto px-4">
          <Link
            to={ROUTE_PATHS.BLOG}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Blog
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="mb-10"
          >
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${getCategoryColor(post.category)}`}>
                {post.category}
              </span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground font-mono">
                <Clock className="w-3 h-3" />
                {post.readTime} de leitura
              </span>
              <span className="text-xs text-muted-foreground">{formatDate(post.date)}</span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold leading-tight text-foreground mb-4">
              {post.title}
            </h1>

            <p className="text-muted-foreground text-lg leading-relaxed">{post.excerpt}</p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-4">
              {post.tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1 text-xs px-2.5 py-1 bg-muted rounded-full border border-border text-muted-foreground">
                  <Tag className="w-2.5 h-2.5" />
                  {tag}
                </span>
              ))}
            </div>
          </motion.header>

          {/* Divider */}
          <div className="border-t border-border mb-10" />

          {/* Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
          >
            <MarkdownContent content={post.content} />
          </motion.div>

          {/* Share */}
          <div className="border-t border-border mt-12 pt-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-foreground">Gostou do artigo?</p>
                <p className="text-xs text-muted-foreground mt-1">Compartilhe com sua rede e aplique na sua estratégia.</p>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(post.title + ' - kamillosantos.com.br/blog/' + post.slug)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-primary/10 border border-primary/30 text-primary px-4 py-2 rounded-lg text-xs font-semibold hover:bg-primary/20 transition-colors"
                >
                  <SiWhatsapp className="w-3.5 h-3.5" />
                  WhatsApp
                </a>
                <button
                  onClick={() => navigator.clipboard?.writeText(window.location.href)}
                  className="flex items-center gap-2 bg-muted border border-border text-muted-foreground px-4 py-2 rounded-lg text-xs font-semibold hover:text-primary transition-colors"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  Copiar link
                </button>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 bg-card border border-border rounded-2xl p-6 text-center">
            <p className="text-sm font-semibold text-foreground mb-1">Quer implementar essas estratégias?</p>
            <p className="text-xs text-muted-foreground mb-4">Agende uma consultoria gratuita com Kamillo Santos.</p>
            <a
              href="https://wa.me/557199999999?text=Li%20seu%20artigo%20e%20quero%20uma%20consultoria"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-xl text-sm font-bold hover:opacity-90 transition-opacity"
            >
              <SiWhatsapp className="w-4 h-4" />
              Agendar Consultoria Gratuita
            </a>
          </div>

          {/* Related Posts */}
          {related.length > 0 && (
            <div className="mt-12">
              <h3 className="text-lg font-bold mb-4">Artigos Relacionados</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="group bg-card border border-border rounded-xl p-4 hover:border-primary/40 transition-all"
                  >
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${getCategoryColor(r.category)}`}>
                      {r.category}
                    </span>
                    <h4 className="font-semibold text-sm mt-2 mb-1 text-foreground group-hover:text-primary transition-colors line-clamp-2">
                      {r.title}
                    </h4>
                    <span className="text-xs text-primary flex items-center gap-1">
                      Ler <ArrowRight className="w-3 h-3" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
